import { ACTION_LABEL } from "@/lib/i18nLabels";
import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Searchable Combobox built on shadcn Command + Popover.
 * Props:
 *  - value: string
 *  - onChange: (v: string) => void
 *  - options: string[]
 *  - placeholder: string
 *  - disabled?: boolean
 *  - testid?: string
 */
export default function Combobox({ value, onChange, options, placeholder = "Select…", disabled, testid }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testid}
          className="w-full justify-between rounded-none font-normal h-10 border-slate-300 hover:bg-white"
        >
          <span className={cn("truncate", !value && "text-slate-400")}>
            {value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 rounded-none w-[--radix-popover-trigger-width]" align="start">
        <Command shouldFilter>
          <CommandInput placeholder={ACTION_LABEL.search} className="h-9" />
          <CommandList>
            <CommandEmpty>No result found.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={(v) => {
                    // v is the lowercased value from cmdk; use opt directly
                    onChange(opt);
                    setOpen(false);
                  }}
                  data-testid={testid ? `${testid}-opt-${opt}` : undefined}
                  className="cursor-pointer rounded-none text-sm"
                >
                  <Check className={cn("mr-2 h-4 w-4", value === opt ? "opacity-100" : "opacity-0")} />
                  {opt}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
